import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import _ from "@lodash";
import withReducer from "app/store/withReducer";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Icon, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FuseLoading from "@fuse/core/FuseLoading";
import reducer from "../store";
import {
  storeCommunicationMessage,
  getCommunicationHistories,
  resetCommunicationHistories,
} from "../../e-commerce/store/orderSlice";
import ShipmentCommunicationHeader from "./ShipmentCommunicationHeader";
import { getShipmentInformation } from "../store/shipmentSlice";
import dispatchMessage, {
  AuraSimplePageRoot,
} from "../../common/AuraFunctions";
import AuraCommunication from "../../common/AuraCommunication";

const ShipmentCommunication = () => {
  const dispatch = useDispatch();
  const routeParams = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submiting, setSubmiting] = useState(false);
  const [noShipment, setNoShipment] = useState(false);
  const [shipmentData, setShipmentData] = useState(null);
  const [communicationHistoryList, setCommunicationHistoryList] = useState([]);
  const [searchParams] = useSearchParams();
  const receiverId = searchParams.get("receiver_id");
  const shipmentId = routeParams?.shipmentId;
  const theme = useTheme();
  const methods = useForm({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    getHistories(true);
    getShipmentInfo();
  }, []);

  useEffect(() => {
    return () => {
      // Reset communication on unmount.
      dispatch(resetCommunicationHistories());
      setNoShipment(false);
    };
  }, []);

  const getHistories = (enableLoading = false) => {
    if (enableLoading) setLoading(true);
    const requestBody = {
      shipment_id: routeParams.shipmentId,
    };
    dispatch(getCommunicationHistories(requestBody))
      .then((communicationResponse) => {
        if (!communicationResponse?.error) {
          setCommunicationHistoryList(
            communicationResponse?.payload?.notes?.[0]
          );
        }
      })
      .finally(() => {
        if (enableLoading) setLoading(false);
      });
  };

  const getShipmentInfo = () => {
    if (shipmentId) {
      dispatch(getShipmentInformation(shipmentId)).then((response) => {
        if (response && !response.error) {
          // If the requested shipment is not exist show message
          if (!response.payload) {
            setNoShipment(true);
          } else {
            setShipmentData(response.payload);
          }
        }
      });
    }
  };

  const getHeaderData = () => {
    return {
      title: shipmentData
        ? `Shipment #${shipmentData?.shipment_no}`
        : "Shipment",
    };
  };

  const handleMarkAsCompleted = () => {
    setSubmiting(true)
    if (receiverId) {
      const payload = {
        shipment_id: shipmentId,
        is_complete: true,
        receiver_id: receiverId,
      };
      dispatch(storeCommunicationMessage(payload)).then((storeResponse) => {
      setSubmiting(false)
        if (!storeResponse?.error) {
          dispatchMessage(
            dispatch,
            "Marked as completed successfully",
            "success"
          );
          navigate(-1);
        }
      });
    }
  };

  /**
   * Wait while shipment data is loading
   */
  if (!shipmentData) {
    return <FuseLoading />;
  }

  /**
   * Show Message if the requested shipment is not exists
   */
  if (noShipment) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center h-full">
        <Typography color="textSecondary" variant="h5">
          There is no such item. Go Back.
        </Typography>
        <Typography
          className="flex items-center sm:mb-12 md:mb-0 pr-0 md:pr-10"
          role="button"
          onClick={() => {
            navigate(-1);
          }}
        >
          <Icon className="text-20">
            {theme.direction === "ltr" ? "arrow_back" : "arrow_forward"}
          </Icon>
        </Typography>
      </div>
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <AuraSimplePageRoot
          header={
            <ShipmentCommunicationHeader
              {...getHeaderData()}
              shipmentData={shipmentData}
            />
          }
          content={
            <div className="w-full p-24">
              <AuraCommunication
                showHeader
                parent="shipmentCommunication"
                showEmptyNotesHistoryMsg
                emptyListMsg = "No Communication Found!"
                showAddNote={false}
                listItems={communicationHistoryList}
                isCommunication
                handleMarkAsCompleted={handleMarkAsCompleted}
                loading={loading}
                submiting={submiting}
              />
            </div>
          }
        />
      </FormProvider>
    </>
  );
};

export default withReducer("backofficeApp", reducer)(ShipmentCommunication);
